'use client'
import * as React from 'react'
import { ChangelogEntry } from '~/app/(mkt)/changelog/fragments'
import { ButtonLink } from '~/sotano-kit/primitives/button'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { parseTab, useTabsStore } from '../../explore/components/tabs/store'
import { clsx } from 'clsx'
import { BaseHubImage } from 'basehub/next-image'

export const ChangelogSidebarItem = ({
  isFirst,
  ...entry
}: ChangelogEntry & { isFirst: boolean }) => {
  const { teamSlug, repoSlug, branchOrCommit } = useDashboardSlugs()
  const addTab = useTabsStore((s) => s.addTab)
  const activeTab = useTabsStore((s) => s.activeTab)
  const isSelected = React.useMemo(() => {
    if (!activeTab) return false
    const parsedActiveTab = parseTab(activeTab)
    return (
      parsedActiveTab?.type === 'changelog' &&
      parsedActiveTab.entryId === entry._id
    )
  }, [activeTab, entry._id])

  const linkRef = React.useRef<HTMLAnchorElement>(null)

  React.useEffect(() => {
    if (isSelected && linkRef.current) {
      linkRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      })
    }
  }, [isSelected])

  return (
    <ButtonLink
      ref={linkRef}
      unstyled
      focusStyled
      href={`/${teamSlug}/${repoSlug}/changelog/${encodeURIComponent(
        branchOrCommit ?? ''
      )}/changelog:${entry._id}`}
      onClick={() =>
        addTab({ key: `changelog:${entry._id}`, partiallyOpen: true })
      }
      onDoubleClick={() =>
        addTab({ key: `changelog:${entry._id}`, partiallyOpen: false })
      }
      className={clsx(
        'grid gap-x-2 cursor-default grid-cols-[auto_1fr] !rounded-lg grid-rows-[1fr_auto] gap-y-0.5 p-2 text-xs last:mb-0 mb-0.5',
        isSelected ? 'bg-dark-shade-hover' : 'hover:bg-dark-border'
      )}
      key={entry._id}
    >
      <AuthorsBubble authors={entry.authorS} />
      <h6 className="text-dark-dim col-start-2">{entry._title}</h6>
      <div className="col-start-2 flex items-center gap-x-1.5 text-dark-muted">
        <span className="text-dark-faint">v{entry.version}</span>
        {isFirst && (
          <span className="flex items-center bg-dark-control-base text-[12px] h-[18px] text-dark-control-base bg-opacity-15 rounded-full px-[7px] leading-[1.3] font-medium">
            New
          </span>
        )}
      </div>
    </ButtonLink>
  )
}

export const AuthorsBubble = ({
  authors,
}: {
  authors: Array<{
    _title: string
    x: string | null
    name: string
    avatar: { url: string; alt: string | null }
  }>
}) => {
  if (authors.length > 1) {
    return (
      <div className="flex items-center justify-center size-6 bg-dark-elevated rounded-full relative">
        {authors.length}
      </div>
    )
  }

  if (!authors[0]) return null
  const { name, avatar } = authors[0]

  return (
    <figure className="flex items-stretch overflow-hidden justify-stretch size-6 border border-dark-elevated rounded-full relative">
      <BaseHubImage
        src={avatar.url}
        alt={avatar.alt ?? name}
        className="object-cover rounded-full"
        width={22}
        height={22}
      />
    </figure>
  )
}
